import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Building2, Store, MapPin, Euro, Star, Filter, ArrowUpRight, Hammer, AlertTriangle } from 'lucide-react';
import { KPICard } from '../components/ui/KPICard';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ZAxis, ComposedChart, Line, Bar, Cell } from 'recharts';
const levels = [{
  id: 'konzern',
  name: 'Konzern-Übersicht',
  icon: Building2
}, {
  id: 'region',
  name: 'Regionen',
  icon: MapPin
}, {
  id: 'markt',
  name: 'Märkte',
  icon: Store
}];
const marketsData = [{
  id: 'hamburg-hafencity',
  name: 'ALDI Nord Hamburg-Hafencity',
  region: 'Nord',
  rating: 4.3,
  ratingBefore: 3.9,
  investment: 850,
  lastRenovation: 2022,
  yearsSinceReno: 2,
  size: 1200
}, {
  id: 'berlin-kreuzberg',
  name: 'ALDI Nord Berlin-Kreuzberg',
  region: 'Ost',
  rating: 3.8,
  ratingBefore: 3.6,
  investment: 120,
  lastRenovation: 2019,
  yearsSinceReno: 5,
  size: 1000
}, {
  id: 'hannover-mitte',
  name: 'ALDI Nord Hannover-Mitte',
  region: 'Nord',
  rating: 4.6,
  ratingBefore: 4.1,
  investment: 620,
  lastRenovation: 2023,
  yearsSinceReno: 1,
  size: 1100
}, {
  id: 'koeln-ehrenfeld',
  name: 'ALDI Nord Köln-Ehrenfeld',
  region: 'West',
  rating: 3.5,
  ratingBefore: 3.5,
  investment: 80,
  lastRenovation: 2017,
  yearsSinceReno: 7,
  size: 900
}, {
  id: 'frankfurt-sachsenhausen',
  name: 'ALDI Nord Frankfurt-Sachsenhausen',
  region: 'Mitte',
  rating: 4.4,
  ratingBefore: 4.0,
  investment: 480,
  lastRenovation: 2022,
  yearsSinceReno: 2,
  size: 1050
}, {
  id: 'dortmund-innenstadt',
  name: 'ALDI Nord Dortmund-Innenstadt',
  region: 'West',
  rating: 3.9,
  ratingBefore: 3.7,
  investment: 220,
  lastRenovation: 2020,
  yearsSinceReno: 4,
  size: 950
}, {
  id: 'leipzig-sued',
  name: 'ALDI Nord Leipzig-Süd',
  region: 'Ost',
  rating: 4.5,
  ratingBefore: 4.0,
  investment: 720,
  lastRenovation: 2023,
  yearsSinceReno: 1,
  size: 1150
}, {
  id: 'muenster-zentrum',
  name: 'ALDI Nord Münster-Zentrum',
  region: 'West',
  rating: 4.2,
  ratingBefore: 3.9,
  investment: 350,
  lastRenovation: 2021,
  yearsSinceReno: 3,
  size: 1000
}, {
  id: 'osnabrueck-zentrum',
  name: 'ALDI Nord Osnabrück-Zentrum',
  region: 'Nord',
  rating: 4.1,
  ratingBefore: 3.8,
  investment: 280,
  lastRenovation: 2021,
  yearsSinceReno: 3,
  size: 980
}, {
  id: 'kiel-zentrum',
  name: 'ALDI Nord Kiel-Zentrum',
  region: 'Nord',
  rating: 4.4,
  ratingBefore: 4.0,
  investment: 420,
  lastRenovation: 2022,
  yearsSinceReno: 2,
  size: 1050
}];
const regionsData = [{
  id: 'nord',
  name: 'ALDI Nord Region Nord',
  rating: 4.2,
  ratingBefore: 3.9,
  investment: 1420,
  lastRenovation: 2022,
  yearsSinceReno: 2,
  size: 4500,
  markets: 3
}, {
  id: 'ost',
  name: 'ALDI Nord Region Ost',
  rating: 3.9,
  ratingBefore: 3.7,
  investment: 840,
  lastRenovation: 2020,
  yearsSinceReno: 4,
  size: 4200,
  markets: 2
}, {
  id: 'west',
  name: 'ALDI Nord Region West',
  rating: 3.87,
  ratingBefore: 3.67,
  investment: 620,
  lastRenovation: 2020,
  yearsSinceReno: 4,
  size: 3500,
  markets: 3
}, {
  id: 'mitte',
  name: 'ALDI Nord Region Mitte',
  rating: 4.4,
  ratingBefore: 4.0,
  investment: 480,
  lastRenovation: 2022,
  yearsSinceReno: 2,
  size: 1600,
  markets: 1
}];
const konzernData = {
  id: 'konzern',
  name: 'ALDI Nord Gesamtkonzern',
  rating: 4.15,
  ratingBefore: 3.87,
  investment: 5410,
  lastRenovation: 2022,
  yearsSinceReno: 2,
  size: 20550,
  markets: 15,
  regions: 4
};
const timeSeriesData = [{
  year: '2018',
  investment: 320,
  avgRating: 3.75
}, {
  year: '2019',
  investment: 450,
  avgRating: 3.82
}, {
  year: '2020',
  investment: 580,
  avgRating: 3.88
}, {
  year: '2021',
  investment: 870,
  avgRating: 3.98
}, {
  year: '2022',
  investment: 1850,
  avgRating: 4.08
}, {
  year: '2023',
  investment: 1340,
  avgRating: 4.15
}];
const calculateCorrelation = (data: Array<{
  investment: number;
  rating: number;
}>) => {
  const n = data.length;
  if (n === 0) return 0;
  const sumX = data.reduce((acc, d) => acc + d.investment, 0);
  const sumY = data.reduce((acc, d) => acc + d.rating, 0);
  const sumXY = data.reduce((acc, d) => acc + d.investment * d.rating, 0);
  const sumX2 = data.reduce((acc, d) => acc + d.investment * d.investment, 0);
  const sumY2 = data.reduce((acc, d) => acc + d.rating * d.rating, 0);
  const numerator = n * sumXY - sumX * sumY;
  const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
  return denominator === 0 ? 0 : numerator / denominator;
};
const CustomScatterTooltip = ({
  active,
  payload
}: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const ratingImprovement = data.rating - data.ratingBefore;
    return <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 text-sm" data-id="element-586">
        <div className="font-semibold text-gray-900 mb-2" data-id="element-587">{data.name}</div>
        <div className="space-y-1" data-id="element-588">
          <div className="flex justify-between gap-4" data-id="element-589">
            <span className="text-gray-500" data-id="element-590">Aktuelles Rating:</span>
            <span className="font-medium" data-id="element-591">{data.rating.toFixed(1)} ★</span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-592">
            <span className="text-gray-500" data-id="element-593">Rating vorher:</span>
            <span className="font-medium" data-id="element-594">
              {data.ratingBefore.toFixed(1)} ★
            </span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-595">
            <span className="text-gray-500" data-id="element-596">Verbesserung:</span>
            <span className="font-medium" style={{
            color: ratingImprovement > 0 ? '#639754' : '#E03C32'
          }} data-id="element-597">
              {ratingImprovement > 0 ? '+' : ''}
              {ratingImprovement.toFixed(2)} ★
            </span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-598">
            <span className="text-gray-500" data-id="element-599">Investition:</span>
            <span className="font-medium" data-id="element-600">
              {data.investment.toLocaleString('de-DE')} T€
            </span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-601">
            <span className="text-gray-500" data-id="element-602">Letzte Renovierung:</span>
            <span className="font-medium" data-id="element-603">{data.lastRenovation}</span>
          </div>
        </div>
      </div>;
  }
  return null;
};
export function InvestitionReputationPage() {
  const [selectedLevel, setSelectedLevel] = useState<'konzern' | 'region' | 'markt'>('markt');
  const getScatterData = () => {
    switch (selectedLevel) {
      case 'markt':
        return marketsData.map(m => ({
          ...m,
          z: m.size
        }));
      case 'region':
        return regionsData.map(r => ({
          ...r,
          z: r.size
        }));
      case 'konzern':
        return [{
          ...konzernData,
          z: konzernData.size
        }];
    }
  };
  const scatterData = getScatterData();
  const correlation = calculateCorrelation(scatterData);
  const avgInvestment = scatterData.reduce((acc, d) => acc + d.investment, 0) / scatterData.length;
  const avgRating = scatterData.reduce((acc, d) => acc + d.rating, 0) / scatterData.length;
  const avgImprovement = scatterData.reduce((acc, d) => acc + (d.rating - d.ratingBefore), 0) / scatterData.length;
  const totalInvestment = scatterData.reduce((acc, d) => acc + d.investment, 0);
  const needsRenovation = [...scatterData].filter(d => d.yearsSinceReno >= 4 && d.rating < avgRating).sort((a, b) => b.yearsSinceReno - a.yearsSinceReno).slice(0, 3);
  const bestROI = [...scatterData].map(d => ({
    ...d,
    roi: (d.rating - d.ratingBefore) / (d.investment / 100)
  })).sort((a, b) => b.roi - a.roi).slice(0, 3);
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-604">
      <Card className="!p-4" data-id="element-605">
        <div className="flex items-center gap-4 flex-wrap" data-id="element-606">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-607">
            <Filter size={16} data-id="element-608" />
            <span data-id="element-609">Ebene:</span>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-610">
            {levels.map(level => {
            const isSelected = selectedLevel === level.id;
            const Icon = level.icon;
            return <button key={level.id} onClick={() => setSelectedLevel(level.id as any)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${isSelected ? 'bg-aldi-blue text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-611">
                  <Icon size={14} data-id="element-612" />
                  {level.name}
                </button>;
          })}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-613">
        <KPICard title="Korrelation Invest ↔ Rating" value={correlation.toFixed(2)} change={correlation > 0.6 ? 'Stark positiv' : correlation > 0.3 ? 'Moderat' : 'Schwach'} trend="up" icon={<TrendingUp size={18} data-id="element-615" />} subtext="Pearson-Koeffizient" data-id="element-614" />
        <KPICard title="Gesamtinvestition" value={`${(totalInvestment / 1000).toFixed(1)}M €`} change="letzte 5 Jahre" trend="up" icon={<Euro size={18} data-id="element-617" />} subtext="alle Filialen" data-id="element-616" />
        <KPICard title="Ø Rating-Verbesserung" value={`+${avgImprovement.toFixed(2)}★`} change="nach Renovierung" trend="up" icon={<Star size={18} data-id="element-619" />} subtext="pro Filiale" data-id="element-618" />
        <KPICard title="Renovierungsbedarf" value={needsRenovation.length.toString()} change=">4 Jahre alt" trend="down" icon={<Hammer size={18} data-id="element-621" />} subtext="Märkte" data-id="element-620" />
      </div>

      <Card data-id="element-622">
        <div className="p-6" data-id="element-623">
          <div className="flex items-center justify-between mb-6" data-id="element-624">
            <div data-id="element-625">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-626">
                Investition vs. Kundenbewertung
              </h3>
              <p className="text-sm text-gray-500" data-id="element-627">
                Blasengröße = Verkaufsfläche (m²) • Investition in Tsd. €
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-500" data-id="element-628">
              <div className="flex items-center gap-1" data-id="element-629">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#639754'
              }} data-id="element-630"></div>
                <span data-id="element-631">Kürzlich renoviert (≤2J)</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-632">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#E03C32'
              }} data-id="element-633"></div>
                <span data-id="element-634">Renovierung nötig (≥5J)</span>
              </div>
            </div>
          </div>
          <div className="h-96" data-id="element-635">
            <ResponsiveContainer width="100%" height="100%" data-id="element-636">
              <ScatterChart margin={{
              top: 20,
              right: 20,
              bottom: 20,
              left: 20
            }} data-id="element-637">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-638" />
                <XAxis type="number" dataKey="investment" name="Investition" domain={[0, 1000]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} tickFormatter={v => `${v}T€`} label={{
                value: 'Investition (Tsd. €)',
                position: 'bottom',
                offset: 0,
                style: {
                  fontSize: 11,
                  fill: '#6b7280'
                }
              }} data-id="element-639" />
                <YAxis type="number" dataKey="rating" name="Rating" domain={[3.2, 5]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} label={{
                value: 'Google Rating (Sterne)',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fontSize: 11,
                  fill: '#6b7280'
                }
              }} data-id="element-640" />
                <ZAxis type="number" dataKey="z" range={[100, 800]} data-id="element-641" />
                <Tooltip content={<CustomScatterTooltip data-id="element-643" />} data-id="element-642" />
                <Scatter name="Standorte" data={scatterData} fill="#00005f" data-id="element-644">
                  {scatterData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.yearsSinceReno <= 2 ? '#639754' : entry.yearsSinceReno <= 4 ? '#FFD301' : '#E03C32'} fillOpacity={0.7} data-id="element-645" />)}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm" data-id="element-646">
            <div className="flex items-center gap-2" data-id="element-647">
              <TrendingUp size={16} style={{
              color: '#639754'
            }} data-id="element-648" />
              <span className="text-gray-600" data-id="element-649">
                Korrelation:{' '}
                <span className="font-semibold" style={{
                color: correlation > 0.5 ? '#639754' : '#FFD301'
              }} data-id="element-650">
                  {(correlation * 100).toFixed(0)}%
                </span>{' '}
                – Höhere Investitionen korrelieren mit besseren Bewertungen
              </span>
            </div>
            <div className="text-gray-500" data-id="element-651">
              Ø Investition: {avgInvestment.toFixed(0)} T€
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-652">
        <Card data-id="element-653">
          <div className="p-6" data-id="element-654">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-655">
              <ArrowUpRight className="text-green-600" size={20} data-id="element-656" /> Beste
              Investitionsrendite (ROI)
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-657">
              Höchste Rating-Verbesserung pro investiertem Euro
            </p>
            <div className="space-y-3" data-id="element-658">
              {bestROI.map((item, index) => {
              const improvement = item.rating - item.ratingBefore;
              return <motion.div key={item.id} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className="flex items-center justify-between p-3 rounded-lg" style={{
                backgroundColor: '#e8f0e5'
              }} data-id="element-659">
                    <div className="flex items-center gap-3" data-id="element-660">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                    backgroundColor: '#639754'
                  }} data-id="element-661">
                        {index + 1}
                      </div>
                      <div data-id="element-662">
                        <div className="font-medium text-gray-900 text-sm" data-id="element-663">
                          {item.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-664">
                          {item.investment.toLocaleString('de-DE')} T€ •
                          Renoviert {item.lastRenovation}
                        </div>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-665">
                      <div className="font-bold text-lg" style={{
                    color: '#639754'
                  }} data-id="element-666">
                        +{improvement.toFixed(2)} ★
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-667">
                        {item.ratingBefore.toFixed(1)} →{' '}
                        {item.rating.toFixed(1)}
                      </div>
                    </div>
                  </motion.div>;
            })}
            </div>
          </div>
        </Card>

        <Card data-id="element-668">
          <div className="p-6" data-id="element-669">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-670">
              <AlertTriangle className="text-red-600" size={20} data-id="element-671" />{' '}
              Renovierungsbedarf
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-672">
              Märkte mit alter Ausstattung und unterdurchschnittlichem Rating
            </p>
            <div className="space-y-3" data-id="element-673">
              {needsRenovation.length > 0 ? needsRenovation.map((item, index) => <motion.div key={item.id} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1
            }} className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#fce8e7'
            }} data-id="element-674">
                    <div className="flex items-center gap-3" data-id="element-675">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                  backgroundColor: '#E03C32'
                }} data-id="element-676">
                        !
                      </div>
                      <div data-id="element-677">
                        <div className="font-medium text-gray-900 text-sm" data-id="element-678">
                          {item.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-679">
                          Letzte Renovierung: {item.lastRenovation} (
                          {item.yearsSinceReno} Jahre)
                        </div>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-680">
                      <div className="font-bold text-lg" style={{
                  color: '#E03C32'
                }} data-id="element-681">
                        {item.rating.toFixed(1)} ★
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-682">
                        {((avgRating - item.rating) / avgRating * 100).toFixed(0)}
                        % unter Ø
                      </div>
                    </div>
                  </motion.div>) : <div className="text-center py-8 text-gray-500" data-id="element-683">
                  Keine Märkte mit dringendem Renovierungsbedarf
                </div>}
            </div>
          </div>
        </Card>
      </div>

      <Card data-id="element-684">
        <div className="p-6" data-id="element-685">
          <div className="flex items-center justify-between mb-6" data-id="element-686">
            <div data-id="element-687">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-688">
                Investitionen und Rating-Entwicklung
              </h3>
              <p className="text-sm text-gray-500" data-id="element-689">
                Jährliche Investitionen und durchschnittliche Bewertung
              </p>
            </div>
            <div className="flex items-center gap-4 text-sm" data-id="element-690">
              <div className="flex items-center gap-2" data-id="element-691">
                <div className="w-3 h-3 rounded-full bg-aldi-blue" data-id="element-692"></div>
                <span className="text-gray-600" data-id="element-693">Investition (Mio. €)</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-694">
                <div className="w-6 h-0.5 bg-aldi-accent" data-id="element-695"></div>
                <span className="text-gray-600" data-id="element-696">Ø Rating</span>
              </div>
            </div>
          </div>
          <div className="h-72" data-id="element-697">
            <ResponsiveContainer width="100%" height="100%" data-id="element-698">
              <ComposedChart data={timeSeriesData} data-id="element-699">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-700" />
                <XAxis dataKey="year" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} data-id="element-701" />
                <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} domain={[0, 2000]} tickFormatter={v => `${(v / 1000).toFixed(1)}M`} label={{
                value: 'Investition (Mio. €)',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fontSize: 11,
                  fill: '#9ca3af'
                }
              }} data-id="element-702" />
                <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} domain={[3.5, 4.5]} label={{
                value: 'Rating',
                angle: 90,
                position: 'insideRight',
                style: {
                  fontSize: 11,
                  fill: '#9ca3af'
                }
              }} data-id="element-703" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }} formatter={(value: number, name: string) => [name === 'investment' ? `${(value / 1000).toFixed(2)} Mio. €` : value.toFixed(2), name === 'investment' ? 'Investition' : 'Ø Rating']} data-id="element-704" />
                <Bar yAxisId="left" dataKey="investment" fill="#00005f" radius={[4, 4, 0, 0]} barSize={40} data-id="element-705" />
                <Line yAxisId="right" type="monotone" dataKey="avgRating" stroke="#009ee3" strokeWidth={3} dot={{
                fill: '#009ee3',
                strokeWidth: 2,
                r: 5
              }} data-id="element-706" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Card>

      <Card data-id="element-707">
        <div className="p-6" data-id="element-708">
          <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-709">
            Detailübersicht{' '}
            {selectedLevel === 'markt' ? 'Märkte' : selectedLevel === 'region' ? 'Regionen' : 'Konzern'}
          </h3>
          <div className="overflow-x-auto" data-id="element-710">
            <table className="w-full text-sm" data-id="element-711">
              <thead data-id="element-712">
                <tr className="border-b border-gray-200" data-id="element-713">
                  <th className="text-left py-3 px-4 font-medium text-gray-500" data-id="element-714">
                    Name
                  </th>
                  {selectedLevel === 'markt' && <th className="text-left py-3 px-4 font-medium text-gray-500" data-id="element-715">
                      Region
                    </th>}
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-716">
                    Rating
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-717">
                    Vorher
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-718">
                    Δ Rating
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-719">
                    Investition
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-720">
                    Renovierung
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-721">
                {scatterData.map((item: any, index) => {
                const improvement = item.rating - item.ratingBefore;
                return <tr key={item.id || index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-722">
                      <td className="py-3 px-4 font-medium text-gray-900" data-id="element-723">
                        {item.name}
                      </td>
                      {selectedLevel === 'markt' && <td className="py-3 px-4 text-gray-500" data-id="element-724">
                          {item.region}
                        </td>}
                      <td className="py-3 px-4 text-right" data-id="element-725">
                        <span className={`font-medium ${item.rating >= 4.3 ? 'text-green-600' : item.rating >= 3.8 ? 'text-gray-900' : 'text-red-600'}`} data-id="element-726">
                          {item.rating.toFixed(1)} ★
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-gray-500" data-id="element-727">
                        {item.ratingBefore.toFixed(1)} ★
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-728">
                        <span className={`font-medium ${improvement > 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-729">
                          {improvement > 0 ? '+' : ''}
                          {improvement.toFixed(2)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900" data-id="element-730">
                        {item.investment.toLocaleString('de-DE')} T€
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-731">
                        <Badge variant={item.yearsSinceReno <= 2 ? 'success' : item.yearsSinceReno <= 4 ? 'warning' : 'danger'} data-id="element-732">
                          {item.lastRenovation}
                        </Badge>
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>;
}